import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { buildUrl, fetchJson } from "../shared/api";
import type { SearchResult, SearchResultType } from "../shared/types/api";

const GROUP_ORDER: SearchResultType[] = ["idm", "module", "connector", "releaseNote"];

const MIN_QUERY_LENGTH = 2;

/**
 * Wraps every occurrence of the query in <mark> (case-insensitive).
 */
function highlightMatch(text: string, query: string): React.ReactNode {
    if (!query) return text;

    const lower = text.toLowerCase();
    const needle = query.toLowerCase();
    const parts: React.ReactNode[] = [];

    let index = 0;
    let match = lower.indexOf(needle);

    while (match !== -1) {
        if (match > index) parts.push(text.slice(index, match));
        parts.push(<mark key={match}>{text.slice(match, match + needle.length)}</mark>);
        index = match + needle.length;
        match = lower.indexOf(needle, index);
    }

    parts.push(text.slice(index));
    return parts;
}

/**
 * Builds the link to the existing detail page for a search result.
 */
function resultLink(result: SearchResult): string {
    switch (result.type) {
        case "releaseNote":
            return `/release-note?module-version=${encodeURIComponent(result.releaseId ?? "")}`;
        case "module":
            return `/moduledetail?repository=${encodeURIComponent(result.repository)}`;
        case "connector":
            return `/connectordetail?repository=${encodeURIComponent(result.repository)}`;
        case "idm": {
            // major is part of the display name, e.g. "IdM 15.x.x"
            const major = result.name?.match(/\d+/)?.[0];
            return major
                ? `/idmdetail?repository=${encodeURIComponent(result.repository)}&major=${encodeURIComponent(major)}`
                : "/dashboard";
        }
        default:
            return "/dashboard";
    }
}

function SearchPage() {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const query = (params.get("q") ?? "").trim();

    const [results, setResults] = useState<SearchResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (query.length < MIN_QUERY_LENGTH) {
            setResults([]);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setError(null);

        (async () => {
            try {
                const data = await fetchJson<SearchResult[]>(
                    buildUrl("/search", { query })
                );
                if (!cancelled) setResults(data);
            } catch (err: unknown) {
                if (!cancelled) setError(t("common.error"));
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [query, t, i18n.language]);

    const grouped: Partial<Record<SearchResultType, SearchResult[]>> = {};
    for (const result of results) {
        (grouped[result.type] ??= []).push(result);
    }

    return (
        <div className="page-card">
            <h2 className="page-title">
                <FontAwesomeIcon icon={faMagnifyingGlass} />{" "}
                {query ? t("search.resultsFor", { query }) : t("search.title")}
            </h2>

            {query.length < MIN_QUERY_LENGTH && <p className="search-hint">{t("search.minLength")}</p>}

            {isLoading && <p>{t("common.loading")}</p>}
            {error && <p className="text-error">{error}</p>}

            {!isLoading && !error && query.length >= MIN_QUERY_LENGTH && results.length === 0 && (
                <p className="search-hint">{t("search.noResults")}</p>
            )}

            {GROUP_ORDER.map((type) => {
                const group = grouped[type];
                if (!group || group.length === 0) return null;

                return (
                    <div key={type} className="search-group">
                        <h3 className="search-group-title">
                            {t(`search.groups.${type}`)} ({group.length})
                        </h3>

                        {group.map((result, index) => (
                            <div key={`${result.type}-${result.releaseId ?? result.repository}-${index}`} className="search-result">
                                <Link to={resultLink(result)}>
                                    {result.type === "releaseNote"
                                        ? highlightMatch(`${result.tableName ?? result.repository} ${result.tagName ?? ""}`, query)
                                        : highlightMatch(result.name ?? result.repository, query)}
                                </Link>

                                {result.type === "releaseNote" && result.releasedAt && (
                                    <span className="search-result__meta">
                                        {" "}
                                        {new Date(result.releasedAt).toLocaleDateString(i18n.language || "cs-CZ")}
                                    </span>
                                )}

                                {result.type !== "releaseNote" && (
                                    <span className="search-result__meta"> {highlightMatch(result.repository, query)}</span>
                                )}

                                {result.snippet && (
                                    <div className="search-snippet">
                                        {result.snippet.split("\n").map((line, lineIndex) => (
                                            <div key={lineIndex}>{highlightMatch(line, query)}</div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                );
            })}
        </div>
    );
}

export default SearchPage;
